import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { ReactNode, SyntheticEvent, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Badge, IconButton, Stack, Theme } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';

import { bottomNavigation } from './bottomNavigationData';
import ActiveSessionsSheet from './ActiveSessionsSheet';

import QRCodeIcon from '../../../assets/icons/qrCodeIcon.svg?react';
import { isReactNative, sendEventToReactNative } from '../../../common/utils/reactNativeHelpers';
import { WebViewEvents } from '../../../common/enums/webViewEvents';
import { useActiveSessions } from '../../../hooks/useActiveSessions';
import { useIdleSessionLocation } from '../../../hooks/useIdleSessionLocation';
import { chargingTabTarget, stationSessionPath } from '../../../utils/activeSessions';

/** Tab value for the picker — it opens a sheet rather than going somewhere. */
const CHARGING_PICKER_VALUE = 'charging-picker';

interface NavItem {
  to?: string;
  value: string;
  icon: ReactNode;
  onClick?: () => void;
}

export default function LabelBottomNavigation() {
  const location = useLocation();
  const navigate = useNavigate();

  const [value, setValue] = useState(location.pathname);
  const [pickerOpen, setPickerOpen] = useState(false);

  const { sessions } = useActiveSessions();
  // Nothing is charging, but the car may still be plugged in (idle/unplug
  // window). Keep the charging tab visible while that session is idle.
  const idleSession = useIdleSessionLocation(sessions.length === 0);

  const handleChange = (
    _event: SyntheticEvent<Element, Event>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newValue: any,
  ) => {
    setValue(newValue);
  };

  const handleOpenCamera = () => {
    sendEventToReactNative(WebViewEvents.OPEN_QR_SCANNER);
  };

  const target = chargingTabTarget(sessions, idleSession);

  const boltIcon = (
    <BoltIcon
      sx={{
        fontSize: 28,
        color: 'green.main',
        animation: 'bn-charging-pulse 1.4s ease-in-out infinite',
        '@keyframes bn-charging-pulse': {
          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
          '50%': { opacity: 0.55, transform: 'scale(0.85)' },
        },
      }}
    />
  );

  // A dedicated charging tab, shown only while a session is active or idle.
  // It is inserted right after Home instead of replacing it. With more than one
  // charge running it counts them and asks which one to open.
  let chargingItem: NavItem | null = null;
  if (target.kind === 'link') {
    chargingItem = { to: target.path, value: target.path, icon: boltIcon };
  } else if (target.kind === 'picker') {
    chargingItem = {
      value: CHARGING_PICKER_VALUE,
      onClick: () => setPickerOpen(true),
      icon: (
        <Badge badgeContent={target.sessions.length} color="success">
          {boltIcon}
        </Badge>
      ),
    };
  }

  const firstLinks = [
    bottomNavigation[0],
    ...(chargingItem ? [chargingItem] : []),
    bottomNavigation[1],
  ];
  const lastLinks = bottomNavigation.slice(2);

  const actionSx = (theme: Theme) => ({
    borderRadius: '100px',
    backgroundColor: 'transparent',
    width: '65px',
    minWidth: 0,
    '&.Mui-selected svg *': {
      fill: theme.palette.green.main,
    },
  });

  const renderAction = (item: NavItem, key: string) =>
    item.to ? (
      <BottomNavigationAction
        key={key}
        component={NavLink}
        to={item.to}
        end
        disableRipple
        value={item.value}
        icon={item.icon}
        sx={actionSx}
      />
    ) : (
      <BottomNavigationAction
        key={key}
        disableRipple
        value={item.value}
        icon={item.icon}
        onClick={item.onClick}
        sx={actionSx}
      />
    );

  return (
    <>
      <BottomNavigation
        value={value}
        onChange={handleChange}
        sx={{
          backgroundColor: 'gray.200',
          width: '100%',
          height: '57px',
        }}
      >
        {firstLinks.map((item, index) => renderAction(item, `first-${index}`))}
        {isReactNative && (
          <Stack sx={{ position: 'relative', width: '60px' }}>
            <IconButton
              sx={(theme: Theme) => ({
                position: 'absolute',
                bottom: '12px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: theme.palette.gray[200],
                border: '1px solid',
                borderColor: theme.palette.green.main,
                borderRadius: '10px',
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.gray[200],
                },
              })}
              onClick={handleOpenCamera}
            >
              <QRCodeIcon />
            </IconButton>
          </Stack>
        )}
        {lastLinks.map((item, index) => renderAction(item, `last-${index}`))}
      </BottomNavigation>
      <ActiveSessionsSheet
        open={pickerOpen}
        sessions={target.kind === 'picker' ? target.sessions : []}
        onClose={() => setPickerOpen(false)}
        onSelect={(session) => {
          setPickerOpen(false);
          navigate(stationSessionPath(session.stationId, session.connectorId));
        }}
      />
    </>
  );
}
